import { useState } from 'react';
import axios from 'axios';
import { FileArchive, Loader2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExportDialog({ open, onClose, owner }) {
  const token = localStorage.getItem('session_token');
  const [executorName, setExecutorName] = useState('');
  const [includeDocs, setIncludeDocs] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/readiness/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { executor_name: executorName, include_documents: includeDocs, ...(owner ? { owner } : {}) },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Family-Readiness-Packet.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your executor packet is downloading.');
      onClose?.();
    } catch {
      toast.error('Could not generate the packet. Please try again.');
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md rounded-3xl bg-white" data-testid="readiness-export-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2C3A47] flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-[#4F86C6]" /> Export for your executor
          </DialogTitle>
          <DialogDescription className="sr-only">Download a summary and documents packet for your executor or attorney.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#8D8880] -mt-1">
          Download a ZIP with a printable summary of your readiness checklist and all your attached documents,
          organized for your executor or attorney.
        </p>

        <div className="mt-2 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[#57534E] mb-1.5">Executor / attorney name (optional)</p>
            <Input data-testid="export-executor-name" value={executorName} onChange={(e) => setExecutorName(e.target.value)}
              placeholder="e.g., Jane Doe, Esq." className="rounded-2xl border-[#EAE7E2] h-11" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer bg-[#FAFBFC] border border-[#EEECE8] rounded-2xl px-4 py-3">
            <input type="checkbox" data-testid="export-include-docs" checked={includeDocs}
              onChange={(e) => setIncludeDocs(e.target.checked)} className="h-4 w-4 accent-[#4F86C6]" />
            <span className="text-sm text-[#57534E]">Include attached documents & photos</span>
          </label>
        </div>

        <div className="flex gap-3 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-full flex-1">Cancel</Button>
          <Button data-testid="export-download-button" onClick={download} disabled={downloading}
            className="rounded-full flex-1 bg-[#4F86C6] hover:bg-[#3A6EA5] text-white">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Download</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
